import {
    RegistrationTabButton,
    RegistrationTabContent,
    RegistrationTabs,
    type RegistrationTransformedValues,
    useRegistrationForm,
} from "@/components/RegistrationForm";
import { useCreateRegistrationMutation } from "@/mutations/registration.ts";
import { useQueryOptionsFactory } from "@/queries";
import { defaultMutationErrorHandler } from "@/utils/api.ts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Alert,
    Box,
    Button,
    Container,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useCallback, useState } from "react";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { auctionId } = Route.useParams();
    const auction = useSuspenseQuery(qof.auction.get(auctionId)).data;
    const { form, errorHandler, activeTab, setActiveTab } = useRegistrationForm({
        auction,
    });
    const createMutation = useCreateRegistrationMutation();
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = useCallback(
        (values: RegistrationTransformedValues) => {
            createMutation.mutate(
                { auctionId, ...values },
                {
                    onSuccess: () => {
                        setSubmitted(true);
                    },
                    onError: defaultMutationErrorHandler,
                },
            );
        },
        [createMutation, auctionId],
    );

    let content: ReactNode;

    if (submitted) {
        content = (
            <Alert severity="success" sx={{ mt: 2 }}>
                Thank you, we have received your registration. You can now close this page.
            </Alert>
        );
    } else if (auction.state !== "registration_open") {
        content = (
            <Alert severity="error" sx={{ mt: 2 }}>
                The registration for this auction has been closed.
            </Alert>
        );
    } else {
        content = (
            <>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2, mb: 4 }}>
                    <RegistrationTabs
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        sealedBid={auction.sealedBid}
                    />
                </Box>

                <form onSubmit={form.handleSubmit(handleSubmit, errorHandler)} noValidate>
                    <Stack spacing={2}>
                        <RegistrationTabContent
                            activeTab={activeTab}
                            auction={auction}
                            control={form.control}
                        />
                        <RegistrationTabButton
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            variant="contained"
                            sx={{ alignSelf: "end" }}
                            loading={createMutation.isPending}
                        />
                    </Stack>
                </form>
            </>
        );
    }

    return (
        <Container sx={{ py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Box sx={{ mr: "auto" }}>
                    <Typography variant="h5">New Bidder Registration</Typography>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                        for {auction.title}
                    </Typography>
                </Box>

                <Button
                    component="a"
                    href="https://wingertlandservices.com/all-properties/"
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{ display: { xs: "none", md: "flex" } }}
                >
                    Back to Property Listing
                </Button>

                <IconButton
                    component="a"
                    href="https://wingertlandservices.com/all-properties/"
                    color="primary"
                    sx={{ display: { xs: "flex", md: "none" } }}
                >
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            {content}
        </Container>
    );
};

export const Route = createFileRoute("/registration/$auctionId")({
    component: Root,
    pendingComponent: () => <LinearProgress />,
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(context.qof.auction.get(params.auctionId));
    },
});
